import {
  APP_MODAL_ROUTES,
  INSIGHTS_HUB_ROUTES,
  LOCATION_MANAGER_ROUTES,
} from '@/navigators/routes';
import {
  CurrentOrdersCallPayload,
  InvoiceDetailViewPayload,
  InvoicePressPayload,
  ItemDetailViewPayload,
  ItemPurchaseHistoryPressPayload,
  LowestPerformingCustomersTogglePressPayload,
  ObiItemPressPayload,
  ObiItemsOfInterestViewPayload,
  OrderDetailViewPayload,
  OrderItemPressPayload,
  OrderManagementPayload,
  OrderManagementTabPayload,
  OrderPressPayload,
  PhonePickerSheetOpenPayload,
  PortalSwitchButtonPressPayload,
  PushNotificationOpenPayload,
  SalesAlertLostItemActionSheetPressPayload,
  SalesAlertViewPayload,
} from './types';

/**
 * Registry for all metric events.
 * Here every user event to be tracked has to be registered, each event specifies its
 * static properties and a schema type for its extra data.
 */
export enum AnalyticEvent {
  CURRENT_ORDERS_CALL = 'insights_hub.hub_current_orders_section.call_phone.press',
  DRAFT_DETAIL_VIEW = 'draft_detail_view',
  INVOICE_DETAIL_VIEW = 'invoice_detail_view',
  INVOICE_PRESS = 'invoice_press',
  ITEM_DETAIL_VIEW = 'item_detail_view',
  ITEM_PURCHASE_HISTORY_PRESS = 'item_purchase_history_press',
  LOWEST_PERFORMING_CUSTOMERS_TOGGLE_PRESS = 'lowest_performing_customers_toggle_press',
  NOTIFICATION_OPEN = 'notification_open',
  OBI_ITEM_PRESS = 'obi_item_of_interest_press',
  OBI_ITEM_VIEW = 'obi_item_of_interest_view',
  ORDER_DETAIL_VIEW = 'order_detail_view',
  ORDER_ITEM_PRESS = 'order_item_press',
  ORDER_MANAGEMENT_TAB = 'order_management_tab',
  ORDER_MANAGEMENT_VIEW = 'order_management_view',
  ORDER_PRESS = 'order_press',
  PHONE_PICKER_SHEET_OPEN = 'insights_hub.hub_current_orders_section.phone_picker_sheet_open',
  PORTAL_SWITCH_BUTTON_PRESS = 'portal_switch_button_press',
  SALES_ALERT_LOST_ITEM_ACTION_SHEET_PRESS = 'sales_alert_lost_item_action_sheet_press',
  SALES_ALERT_VIEW = 'sales_alert_view',
}

export interface AnalyticMetadata {
  description: string;
  eventName: string;
  origin?: string | string[];
}

export interface AnalyticPayloads {
  [AnalyticEvent.PHONE_PICKER_SHEET_OPEN]: PhonePickerSheetOpenPayload;
  [AnalyticEvent.CURRENT_ORDERS_CALL]: CurrentOrdersCallPayload;
  [AnalyticEvent.OBI_ITEM_VIEW]: ObiItemsOfInterestViewPayload;
  [AnalyticEvent.NOTIFICATION_OPEN]: PushNotificationOpenPayload;
  [AnalyticEvent.SALES_ALERT_VIEW]: SalesAlertViewPayload;
  [AnalyticEvent.LOWEST_PERFORMING_CUSTOMERS_TOGGLE_PRESS]: LowestPerformingCustomersTogglePressPayload;
  [AnalyticEvent.ORDER_PRESS]: OrderPressPayload;
  [AnalyticEvent.INVOICE_PRESS]: InvoicePressPayload;
  [AnalyticEvent.ORDER_DETAIL_VIEW]: OrderDetailViewPayload;
  [AnalyticEvent.INVOICE_DETAIL_VIEW]: InvoiceDetailViewPayload;
  [AnalyticEvent.DRAFT_DETAIL_VIEW]: OrderDetailViewPayload;
  [AnalyticEvent.ORDER_MANAGEMENT_VIEW]: OrderManagementPayload;
  [AnalyticEvent.ORDER_MANAGEMENT_TAB]: OrderManagementTabPayload;
  [AnalyticEvent.ITEM_DETAIL_VIEW]: ItemDetailViewPayload;
  [AnalyticEvent.ORDER_ITEM_PRESS]: OrderItemPressPayload;
  [AnalyticEvent.OBI_ITEM_PRESS]: ObiItemPressPayload;
  [AnalyticEvent.SALES_ALERT_LOST_ITEM_ACTION_SHEET_PRESS]: SalesAlertLostItemActionSheetPressPayload;
  [AnalyticEvent.ITEM_PURCHASE_HISTORY_PRESS]: ItemPurchaseHistoryPressPayload;
  [AnalyticEvent.PORTAL_SWITCH_BUTTON_PRESS]: PortalSwitchButtonPressPayload;
}

export const eventsMetadata: { [K in AnalyticEvent]: AnalyticMetadata } = {
  [AnalyticEvent.INVOICE_PRESS]: {
    description: 'When an invoice is pressed',
    eventName: 'Invoice Pressed',
    origin: INSIGHTS_HUB_ROUTES.OrderManagement,
  },
  [AnalyticEvent.PHONE_PICKER_SHEET_OPEN]: {
    description: 'When the Phone Picker Bottom Sheet is opened',
    eventName: 'Opened Phone Picker Bottom Sheet',
    origin: [
      INSIGHTS_HUB_ROUTES.Base,
      INSIGHTS_HUB_ROUTES.DraftDetails,
      INSIGHTS_HUB_ROUTES.OrderDetails,
    ],
  },
  [AnalyticEvent.CURRENT_ORDERS_CALL]: {
    description: 'When the call button is pressed inside Insights Hub',
    eventName: 'Call Pressed',
    origin: [
      INSIGHTS_HUB_ROUTES.Base,
      INSIGHTS_HUB_ROUTES.DraftDetails,
      INSIGHTS_HUB_ROUTES.OrderDetails,
    ],
  },
  [AnalyticEvent.OBI_ITEM_VIEW]: {
    description: 'Every time an Item of Interest is rendered on the screen',
    eventName: 'Viewed OBI Items of Interest',
    origin: LOCATION_MANAGER_ROUTES.ViewAccount,
  },
  [AnalyticEvent.OBI_ITEM_PRESS]: {
    description: 'Every time an Item of Interest is pressed',
    eventName: 'Pressed OBI Items of Interest',
    origin: LOCATION_MANAGER_ROUTES.ViewAccount,
  },
  [AnalyticEvent.NOTIFICATION_OPEN]: {
    description: 'When a push notification is opened',
    eventName: 'Opened Push Notification',
  },
  [AnalyticEvent.SALES_ALERT_LOST_ITEM_ACTION_SHEET_PRESS]: {
    description: 'When the Lost Item Action Sheet support info is pressed',
    eventName: 'Pressed Lost Item Action Sheet Support Info',
    origin: LOCATION_MANAGER_ROUTES.ViewAccount,
  },
  // event will also be triggered from the Insights Hub in the future
  [AnalyticEvent.SALES_ALERT_VIEW]: {
    description: 'When a Sales Alert is viewed',
    eventName: 'Viewed Sales Alert',
    origin: LOCATION_MANAGER_ROUTES.ViewAccount,
  },
  [AnalyticEvent.LOWEST_PERFORMING_CUSTOMERS_TOGGLE_PRESS]: {
    description: 'When the Lowest Performing Customers Toggle is pressed',
    eventName: 'Lowest Performing Customers Toggle Pressed',
  },
  [AnalyticEvent.ORDER_PRESS]: {
    description: 'When an order is pressed',
    eventName: 'Order Pressed',
    origin: [INSIGHTS_HUB_ROUTES.Base, INSIGHTS_HUB_ROUTES.OrderManagement],
  },
  [AnalyticEvent.ORDER_DETAIL_VIEW]: {
    description: 'When a Order Detail is viewed',
    eventName: 'Order Detail Viewed',
    origin: INSIGHTS_HUB_ROUTES.OrderDetails,
  },
  [AnalyticEvent.INVOICE_DETAIL_VIEW]: {
    description: 'When an Invoice Detail is viewed',
    eventName: 'Invoice Detail Viewed',
    origin: INSIGHTS_HUB_ROUTES.InvoiceDetails,
  },
  [AnalyticEvent.DRAFT_DETAIL_VIEW]: {
    description: 'When a Draft Detail is viewed',
    eventName: 'Draft Detail Viewed',
    origin: INSIGHTS_HUB_ROUTES.DraftDetails,
  },
  [AnalyticEvent.ORDER_MANAGEMENT_VIEW]: {
    description: 'When the Order Management screen is viewed',
    eventName: 'Order Management Viewed',
    origin: INSIGHTS_HUB_ROUTES.OrderManagement,
  },
  [AnalyticEvent.ORDER_MANAGEMENT_TAB]: {
    description: 'When the Order Management tab is changed',
    eventName: 'Order Management Tab Changed',
    origin: INSIGHTS_HUB_ROUTES.OrderManagement,
  },
  [AnalyticEvent.ITEM_DETAIL_VIEW]: {
    description: 'When an Item Detail is viewed',
    eventName: 'Viewed Item Detail',
    origin: APP_MODAL_ROUTES.ItemDetails,
  },
  [AnalyticEvent.ORDER_ITEM_PRESS]: {
    description: 'When an Order Item is pressed',
    eventName: 'Pressed Order Item',
    origin: [
      INSIGHTS_HUB_ROUTES.OrderDetails,
      INSIGHTS_HUB_ROUTES.DraftDetails,
    ],
  },
  [AnalyticEvent.ITEM_PURCHASE_HISTORY_PRESS]: {
    description: 'When an Item Purchase History is pressed',
    eventName: 'Pressed Item Purchase History',
    origin: APP_MODAL_ROUTES.ItemDetails,
  },
  [AnalyticEvent.PORTAL_SWITCH_BUTTON_PRESS]: {
    description: 'When the portal switch button is pressed',
    eventName: 'Portal Switch Button Pressed',
  },
};
